import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TournamentService } from '../../services/tournament.service';
import { ClubService } from '../../services/club.service';
import { CourtService } from '../../services/court.service';
import { CreateTournamentRequest } from '../../model/tournament';

interface CourtOption { id: number; label: string; }

@Component({
  selector: 'app-tournament-create',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './tournament-create.component.html',
  styleUrl: './tournament-create.component.css',
})
export class TournamentCreateComponent implements OnInit {
  form: FormGroup;
  clubs: { id: number; name: string }[] = [];
  courts: CourtOption[] = [];
  selectedCourtIds: number[] = [];
  courtsLoading = false;
  showHelp = false;
  errorMessage = '';
  submitting = false;

  readonly maxTeamOptions = [4, 6, 8, 12, 16, 32, 64];

  readonly levels = [
    { value: 1, label: 'Beginner' },
    { value: 2, label: 'Intermediate' },
    { value: 3, label: 'Advanced' },
    { value: 4, label: 'Competitive' },
    { value: 5, label: 'Open to All' },
  ];

  readonly teamFormats = [
    { value: 1, label: 'Pairs (players register with a partner)' },
    { value: 2, label: 'Individuals (organizer pairs at draw)' },
  ];

  constructor(
    private fb: FormBuilder,
    private tournamentService: TournamentService,
    private clubService: ClubService,
    private courtService: CourtService,
    private router: Router,
  ) {
    this.form = this.fb.group({
      name:                     ['', [Validators.required, Validators.maxLength(120)]],
      club_id:                  [null, [Validators.required]],
      start_date:               ['',   [Validators.required]],
      start_time:               ['09:00', [Validators.required]],
      registration_deadline_date: ['', [Validators.required]],
      registration_deadline_time: ['20:00', [Validators.required]],
      max_teams:                [8,  [Validators.required, Validators.min(4), Validators.max(64)]],
      admission_price:          [0,  [Validators.required, Validators.min(0)]],
      level:                    [2,  [Validators.required]],
      team_format:              [1,  [Validators.required]],
      prize_description:        ['', [Validators.maxLength(300)]],
      court_ids:                [[], [Validators.required, Validators.minLength(1)]],
    });
  }

  ngOnInit(): void {
    this.clubService.getClubs().subscribe(rows => { this.clubs = rows as any; });
    this.form.get('club_id')!.valueChanges.subscribe(clubId => this.loadCourts(clubId));
  }

  private loadCourts(clubId: number | null): void {
    this.courts = [];
    this.selectedCourtIds = [];
    this.form.patchValue({ court_ids: [] });
    if (!clubId) return;

    this.courtsLoading = true;
    this.courtService.getCourtsForClub(clubId).subscribe({
      next: rows => {
        this.courts = (rows ?? []).map(c => ({
          id: c.id,
          label: c.name?.trim() ? c.name : `Court ${c.id}`,
        }));
        this.courtsLoading = false;
      },
      error: () => { this.courtsLoading = false; },
    });
  }

  isCourtSelected(id: number): boolean {
    return this.selectedCourtIds.includes(id);
  }

  toggleCourt(id: number): void {
    if (this.selectedCourtIds.includes(id)) {
      this.selectedCourtIds = this.selectedCourtIds.filter(x => x !== id);
    } else {
      this.selectedCourtIds = [...this.selectedCourtIds, id];
    }
    this.form.patchValue({ court_ids: this.selectedCourtIds });
  }

  submit(): void {
    if (this.form.invalid) return;
    this.errorMessage = '';
    this.submitting = true;

    const v = this.form.value;
    const body: CreateTournamentRequest = {
      name: v.name,
      club_id: v.club_id,
      start_date: `${v.start_date}T${v.start_time}:00`,
      registration_deadline: `${v.registration_deadline_date}T${v.registration_deadline_time}:00`,
      max_teams: v.max_teams,
      admission_price: v.admission_price,
      level: v.level,
      team_format: v.team_format,
      prize_description: v.prize_description || undefined,
      court_ids: v.court_ids,
    };

    this.tournamentService.create(body).subscribe({
      next: t => {
        this.submitting = false;
        this.router.navigate(['/tournaments', t.id]);
      },
      error: err => {
        this.submitting = false;
        this.errorMessage = typeof err?.error === 'string' ? err.error : 'Failed to create tournament.';
      },
    });
  }
}
